import { useEffect, useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import type { ObjectSchemaPropertyResponse } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/object-schema-property.tsx';
import type { SchemaId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { getObjectSchemaPropertyUrl } from '../../common/utils.tsx';

type ServiceProps = {
	objectSchemaId: SchemaId;
};

export const useObjectSchemaPropertyService = ({ objectSchemaId }: ServiceProps) => {
	const { workspaceId } = useWorkspaceContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const request = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
		async (props) => {
			if (workspaceId) {
				try {
					const response: ObjectSchemaPropertyResponse = await performGetRequest(
						getObjectSchemaPropertyUrl(workspaceId, props.objectSchemaId),
					);

					fireTrackAnalytics(createAnalyticsEvent({}), 'fetchObjectSchemaProperty succeeded');

					return response;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireCmdbErrorAnalytics({
						error,
						meta: {
							id: 'fetchObjectSchemaProperty',
							packageName: 'jiraServicedeskInsightObjectServices',
							teamName: 'falcons',
						},
					});
					throw error;
				}
			}
			return undefined;
		},
		[workspaceId, createAnalyticsEvent],
	);

	const {
		loading,
		error,
		data,
		fetch: fetchObjectSchemaProperty,
	} = useService<ObjectSchemaPropertyResponse, ServiceProps>(request);

	useEffect(() => {
		if (loading === false && error === undefined && data === undefined) {
			fetchObjectSchemaProperty({ objectSchemaId });
		}
	}, [loading, error, data, fetchObjectSchemaProperty, objectSchemaId]);

	return [{ loading, error, data }, { fetchObjectSchemaProperty }] as const;
};
